import React, {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import Layout from '../components/Layout';
import {api} from '../../api';

export default function Timesheets() {
  const [timesheets, setTimesheets] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [filterEmployee, setFilterEmployee] = useState('');
  const [filterStatus, setFilterStatus] = useState('DRAFT');
  const [activeEntries, setActiveEntries] = useState([]);
  const [showActive, setShowActive] = useState(false);
  const [corrections, setCorrections] = useState([]);
  const [showCorrections, setShowCorrections] = useState(false);
  const [showClockIn, setShowClockIn] = useState(false);
  const [clockInEmployeeId, setClockInEmployeeId] = useState('');
  const [userMap, setUserMap] = useState({});

  const loadActiveEntries = () => {
    api('/timesheets/clock/active-entries').then(r => r.ok ? r.json() : []).then(setActiveEntries);
  };

  const loadCorrections = () => {
    api('/timesheets/time-corrections?status=PENDING').then(r => r.ok ? r.json() : []).then(setCorrections);
  };

  useEffect(() => {
    api('/hcm/employees').then(r => r.ok ? r.json() : []).then(setEmployees);
    api('/tenant-users').then(r => r.ok ? r.json() : []).then(list => {
      const map = {};
      list.forEach(u => { map[u.userId] = `${u.firstName} ${u.lastName}`.trim() || u.username; });
      setUserMap(map);
    }).catch(() => {});
    loadActiveEntries();
    loadCorrections();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams();
    if (filterEmployee) params.set('employeeId', filterEmployee);
    if (filterStatus) params.set('status', filterStatus);
    const qs = params.toString();
    api(`/timesheets/timesheets${qs ? '?' + qs : ''}`).then(r => r.ok ? r.json() : []).then(setTimesheets);
  }, [filterEmployee, filterStatus]);

  const handleClockOut = async (employeeId) => {
    const res = await api(`/timesheets/clock/out/${employeeId}`, { method: 'POST' });
    if (res.ok) loadActiveEntries();
  };

  const handleClockInEmployee = async (e) => {
    e.preventDefault();
    if (!clockInEmployeeId) return;
    const res = await api(`/timesheets/clock/in/${clockInEmployeeId}`, { method: 'POST' });
    if (res.ok) { setShowClockIn(false); setClockInEmployeeId(''); loadActiveEntries(); }
  };

  const handleApproveCorrection = async (id) => {
    const res = await api(`/timesheets/time-corrections/${id}/approve`, { method: 'POST' });
    if (res.ok) loadCorrections();
  };

  const handleDenyCorrection = async (id) => {
    const res = await api(`/timesheets/time-corrections/${id}/deny`, { method: 'POST' });
    if (res.ok) loadCorrections();
  };

  const fmtTime = (iso) => iso ? new Date(iso).toLocaleString() : '—';

  const statusBadge = (status) => {
    const cls = status === 'APPROVED' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800';
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${cls}`}>{status}</span>;
  };

  return (
    <Layout>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Timesheets</h2>
        <Link to="/timesheets/new" className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700">New Timesheet</Link>
        <button onClick={() => { setShowClockIn(true); setClockInEmployeeId(''); }} className="px-4 py-2 border border-teal-600 text-teal-600 rounded-lg hover:bg-teal-50">Clock In Employee</button>
      </div>

      {corrections.length > 0 && (
        <div className="bg-white rounded-lg shadow mb-6">
          <button onClick={() => setShowCorrections(!showCorrections)}
            className="w-full p-4 md:px-6 flex items-center justify-between hover:bg-gray-50">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-100 text-amber-800 text-xs font-bold">{corrections.length}</span>
              <span className="text-sm font-semibold text-gray-900">Pending Time Corrections</span>
            </div>
            <svg className={`w-5 h-5 text-gray-400 transition-transform ${showCorrections ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
          </button>
          {showCorrections && (
            <div className="border-t divide-y divide-gray-200">
              {corrections.map(c => (
                <div key={c.id} className="px-4 md:px-6 py-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-gray-900 font-medium">{c.employeeName} — {c.entryDate}</p>
                      <p className="text-sm text-gray-500 mt-1">
                        Current: {fmtTime(c.currentClockInTime)} → {fmtTime(c.currentClockOutTime)}
                      </p>
                      <p className="text-sm text-teal-700 mt-1">
                        Requested: {fmtTime(c.requestedClockInTime)} → {fmtTime(c.requestedClockOutTime)}
                      </p>
                      <p className="text-sm text-gray-500 mt-1 italic">"{c.reason}"</p>
                    </div>
                    <div className="flex gap-2 ml-4 shrink-0">
                      <button onClick={() => handleApproveCorrection(c.id)} className="px-3 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm">Approve</button>
                      <button onClick={() => handleDenyCorrection(c.id)} className="px-3 py-1 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 text-sm">Deny</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeEntries.length > 0 && (
        <div className="bg-white rounded-lg shadow mb-6">
          <button onClick={() => setShowActive(!showActive)}
            className="w-full p-4 md:px-6 flex items-center justify-between hover:bg-gray-50">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-100 text-green-800 text-xs font-bold">{activeEntries.length}</span>
              <span className="text-sm font-semibold text-gray-900">Currently Clocked In</span>
            </div>
            <svg className={`w-5 h-5 text-gray-400 transition-transform ${showActive ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
          </button>
          {showActive && (
            <div className="border-t divide-y divide-gray-200">
              {[...activeEntries].sort((a, b) => new Date(b.startAt) - new Date(a.startAt)).map(e => (
                <div key={e.id} className="px-4 md:px-6 py-3 flex items-center justify-between">
                  <div>
                    <p className="text-gray-900 font-medium">{e.employeeName}</p>
                    <p className="text-sm text-gray-500">Since {new Date(e.startAt).toLocaleString()}</p>
                    {e.clockInBy && e.clockInBy !== e.employeeId && (
                      <p className="text-xs text-amber-600 mt-0.5">Clocked in by {userMap[e.clockInBy] || 'another user'}</p>
                    )}
                  </div>
                  <button onClick={() => handleClockOut(e.employeeId)} className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 text-sm">
                    Clock Out
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="flex gap-3 mb-4">
        <select value={filterEmployee} onChange={e => setFilterEmployee(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500">
          <option value="">All Employees</option>
          {employees.map(e => <option key={e.id} value={e.id}>{e.firstName} {e.lastName}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500">
          <option value="">All Statuses</option>
          <option value="DRAFT">Draft</option>
          <option value="APPROVED">Approved</option>
        </select>
      </div>

      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">Period</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hours</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {timesheets.map(t => (
              <tr key={t.id} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <Link to={`/timesheets/${t.id}`} className="text-teal-600 hover:text-teal-800 font-medium">{t.employeeName}</Link>
                </td>
                <td className="px-6 py-4 text-gray-600 hidden sm:table-cell">{t.startDate} — {t.endDate}</td>
                <td className="px-6 py-4 text-gray-900 font-medium">{t.totalHours != null ? Number(t.totalHours).toFixed(2) : '—'}</td>
                <td className="px-6 py-4">{statusBadge(t.status)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {timesheets.length === 0 && <div className="text-center py-12 text-gray-500">No timesheets found.</div>}
      </div>

      {showClockIn && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <form onSubmit={handleClockInEmployee} className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Clock In Employee</h3>
            <select autoFocus value={clockInEmployeeId} onChange={e => setClockInEmployeeId(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500">
              <option value="">Select an employee...</option>
              {employees.filter(e => !activeEntries.some(a => a.employeeId === e.id)).map(e => (
                <option key={e.id} value={e.id}>{e.firstName} {e.lastName}</option>
              ))}
            </select>
            <div className="flex justify-end gap-3 mt-4">
              <button type="button" onClick={() => setShowClockIn(false)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button type="submit" disabled={!clockInEmployeeId} className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50">Clock In</button>
            </div>
          </form>
        </div>
      )}
    </Layout>
  );
}
